import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Package, MapPin, Camera, Clock, User, AlertCircle, Loader2, MessageSquare } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const formatDuration = (secs) => {
  const hrs = Math.floor(secs / 3600);
  const mins = Math.floor((secs % 3600) / 60);
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
};

// Normalises the manual-report payload (which carries job + worker_photos)
// into the legacy auto-report shape this page already knows how to render.
// Keeps the JSX below unchanged. Fields not present on a manual report
// (route GPS, duration, distance) fall back to safe defaults so the stat
// cards don't blow up with NaN.
const _adaptManualReport = (mr) => {
  const job = mr.job || {};
  // Combine admin marks attached to the report doc + aggregated worker
  // manual_delivery_locations from tracking_progress (geofenced on the
  // backend). Manual marks render as purple letter-labelled markers in
  // the static map — the green dots are GPS-confirmed deliveries only.
  const reportMarks = (mr.delivery_locations || []).map((m, i) => ({
    id: m.id || `m-${i}`,
    position: m.position || (m.lat != null && m.lng != null ? [m.lat, m.lng] : null),
    lat: m.lat,
    lng: m.lng,
  }));
  const jobMarks = (job.aggregated_manual_marks || []).map((m, i) => ({
    id: `job-m-${i}`,
    position: m.position || (m.lat != null && m.lng != null ? [m.lat, m.lng] : null),
    lat: m.lat,
    lng: m.lng,
  }));
  const marks = [...reportMarks, ...jobMarks].filter(m => m.position || (m.lat != null && m.lng != null));

  // Resolve GPS-confirmed delivered houses → list of {lat, lng} so the
  // static map renderer can drop a green dot per house. We trust the
  // job's own delivered_house_ids first (snapshot at publish), falling
  // back to the parent job's list if the report doesn't carry one.
  const deliveredIds = new Set(
    job.delivered_house_ids || mr.delivered_house_ids || [],
  );
  const houseById = new Map();
  for (const h of (job.cached_house_locations || [])) {
    if (h && h.id != null) houseById.set(String(h.id), h);
  }
  const deliveredPoints = [];
  for (const id of deliveredIds) {
    const h = houseById.get(String(id));
    if (!h) continue;
    const lat = h.lat;
    const lng = h.lng ?? h.lon;
    if (lat == null || lng == null) continue;
    deliveredPoints.push({ lat, lng });
  }

  return {
    // Identity
    id: mr.id,
    job_title: mr.title || mr.job_title || job.title || 'Delivery Report',
    client_name: mr.client_name || job.client_name || job.client || '',
    // Stats
    deliveries_count: mr.deliveries_count ?? 0,
    distance: 0,
    duration: 0,
    delivery_person: mr.created_by_name || 'Local Flyer Delivery',
    completed_at: mr.published_at || mr.updated_at || mr.created_at,
    // Map context — pulled from the parent job by the public endpoint
    delivery_boundary: job.delivery_boundary || [],
    route_path: [],
    manual_delivery_locations: marks,
    // GPS-confirmed delivered houses (resolved to {lat,lng})
    delivered_points: deliveredPoints,
    // Worker / inline photos
    photo_urls: (mr.worker_photos || []).map(p => p.url),
    // Admin-uploaded delivery photos (attached via the report form, stored
    // under report.photos). URLs may be relative — prefix with the API
    // base when needed.
    admin_photos: (mr.photos || []).map(p => ({
      id: p.id,
      url: (p.url || '').startsWith('http') ? p.url : `${API}${p.url}`,
    })),
    // Admin-uploaded custom map snapshots — render BEFORE or instead of
    // the auto-generated static map. URLs may be relative (`/api/...`),
    // so prefix with the API base when needed for the public viewer.
    custom_map_snapshots: (mr.custom_map_snapshots || []).map(s => ({
      id: s.id,
      url: s.url?.startsWith('http') ? s.url : `${API}${s.url}`,
    })),
    // Auditor / summary content
    auditor_notes_list: [],
    summary: mr.summary || '',
    issues_text: mr.issues_text || '',
    issues_checked: mr.issues_checked || [],
    _isManual: true,
  };
};

const ClientReportView = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const isManual = (searchParams.get('type') || '').toLowerCase() === 'manual';
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const endpoint = isManual
          ? `${API}/api/public/manual-report/${token}`
          : `${API}/api/public/report/${token}`;
        const res = await axios.get(endpoint);
        setReport(isManual ? _adaptManualReport(res.data) : res.data);
      } catch (err) {
        if (err.response?.status === 410) {
          setError('expired');
        } else if (err.response?.status === 404) {
          setError('not_found');
        } else {
          setError('error');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [token, isManual]);

  // Build Google Maps Static API URL for the delivery zone.
  // Adds purple markers for any admin-credited or worker manual delivery
  // locations so the client sees the same dots their workers' map shows.
  const getStaticMapUrl = () => {
    if (!report || !GOOGLE_MAPS_KEY) return null;
    const boundary = report.delivery_boundary || [];
    const route = report.route_path || [];
    const marks = report.manual_delivery_locations || [];
    const delivered = report.delivered_points || [];
    if (boundary.length === 0 && route.length === 0 && marks.length === 0 && delivered.length === 0) return null;

    let url = `https://maps.googleapis.com/maps/api/staticmap?size=800x400&maptype=roadmap&key=${GOOGLE_MAPS_KEY}`;

    if (boundary.length > 0) {
      const pathStr = boundary.map(p => `${p[0]},${p[1]}`).join('|');
      const closedPath = `${pathStr}|${boundary[0][0]},${boundary[0][1]}`;
      url += `&path=color:0x7c3aedaa|weight:2|fillcolor:0x7c3aed22|${closedPath}`;
    }

    if (route.length > 0) {
      // Downsample route for URL length limit
      const step = Math.max(1, Math.floor(route.length / 80));
      const sampled = route.filter((_, i) => i % step === 0 || i === route.length - 1);
      const routeStr = sampled.map(p => {
        const lat = Array.isArray(p) ? p[0] : p.lat;
        const lng = Array.isArray(p) ? p[1] : p.lng;
        return `${lat},${lng}`;
      }).join('|');
      url += `&path=color:0xf97316ff|weight:3|${routeStr}`;
    }

    // GPS-confirmed delivered houses → tiny amber dots. Static Maps API
    // caps the full URL near 8 KB, so we downsample to ~200 dots when a
    // job has thousands of houses (still gives a dense visual coverage
    // pattern) and emit them as a single `markers=` param (shared style)
    // to keep the URL short. Color (`#fbbf24`) intentionally matches the
    // delivered-house dot color on the customer tracking map — otherwise
    // green on one map and amber on another would conflict (green =
    // undelivered on the tracking map).
    if (delivered.length > 0) {
      const cap = 200;
      let pool = delivered;
      if (delivered.length > cap) {
        const step = delivered.length / cap;
        pool = [];
        for (let i = 0; i < cap; i += 1) pool.push(delivered[Math.floor(i * step)]);
      }
      // Tiny amber dots, no label (labels require alphanumeric +
      // disallow tiny size).
      const dotsStr = pool
        .map(p => `${p.lat},${p.lng}`)
        .join('|');
      url += `&markers=size:tiny|color:0xfbbf24|${dotsStr}`;
    }

    // For the customer report we render every manual mark as an
    // additional amber dot (same color as GPS-confirmed deliveries).
    // Clients don't need the "admin-marked vs GPS" distinction — they
    // just want to see "X deliveries happened across this zone". Worker
    // manual taps + any remaining admin marks all collapse into the
    // amber set. Backend already strips `is_admin_only` legacy strays
    // upstream via `_filter_marks_for_clients`, so we can trust whatever
    // arrives here.
    const markPoints = marks
      .map((m) => {
        const lat = Array.isArray(m.position) ? m.position[0] : (m.lat != null ? m.lat : null);
        const lng = Array.isArray(m.position) ? m.position[1] : (m.lng != null ? m.lng : null);
        if (lat == null || lng == null) return null;
        return { lat, lng };
      })
      .filter(Boolean);
    if (markPoints.length > 0) {
      const cap = 200;
      let pool = markPoints;
      if (markPoints.length > cap) {
        const step = markPoints.length / cap;
        pool = [];
        for (let i = 0; i < cap; i += 1) pool.push(markPoints[Math.floor(i * step)]);
      }
      const dotsStr = pool.map((p) => `${p.lat},${p.lng}`).join('|');
      url += `&markers=size:tiny|color:0xfbbf24|${dotsStr}`;
    }

    return url;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error === 'expired') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <AlertCircle size={48} className="text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Report Link Expired</h1>
          <p className="text-gray-500">This delivery report link has expired. Please contact your delivery provider for an updated link.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Report Not Found</h1>
          <p className="text-gray-500">This report link is invalid or no longer available.</p>
        </div>
      </div>
    );
  }

  const staticMapUrl = getStaticMapUrl();
  const completedDate = report.completed_at ? new Date(report.completed_at).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit'
  }) : '';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-700 to-purple-500 text-white">
        <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
          <p className="text-purple-200 text-sm font-medium mb-1">Delivery Report</p>
          <h1 className="text-2xl sm:text-3xl font-bold">{report.job_title}</h1>
          {report.client_name && <p className="text-purple-100 mt-1">Prepared for {report.client_name}</p>}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 text-center" data-testid="stat-deliveries">
            <Package size={24} className="text-orange-500 mx-auto mb-2" />
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{report.deliveries_count}</p>
            <p className="text-xs sm:text-sm text-gray-500">Deliveries</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 text-center" data-testid="stat-distance">
            <MapPin size={24} className="text-blue-500 mx-auto mb-2" />
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{(report.distance || 0).toFixed(2)}</p>
            <p className="text-xs sm:text-sm text-gray-500">Miles</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 text-center" data-testid="stat-duration">
            <Clock size={24} className="text-green-500 mx-auto mb-2" />
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{formatDuration(report.duration || 0)}</p>
            <p className="text-xs sm:text-sm text-gray-500">Duration</p>
          </div>
        </div>

        {/* Details */}
        <div className="bg-white rounded-xl shadow-md p-5 mb-6">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1.5">
              <User size={16} className="text-purple-500" />
              <span><span className="font-medium text-gray-900">{report.delivery_person}</span></span>
            </div>
            {completedDate && (
              <div className="flex items-center gap-1.5">
                <Clock size={16} className="text-purple-500" />
                <span>{completedDate}</span>
              </div>
            )}
          </div>
        </div>

        {/* Delivery Zone Map */}
        {staticMapUrl && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6" data-testid="report-map">
            <div className="px-5 py-3 border-b">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <MapPin size={18} className="text-purple-600" />
                Delivery Zone & GPS Route
              </h2>
              {(report.delivered_points || []).length + (report.manual_delivery_locations || []).length > 0 && (
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#fbbf24' }}></span>
                    Amber dot = delivery completed
                  </span>
                  <span className="text-gray-400">
                    {(report.delivered_points || []).length + (report.manual_delivery_locations || []).length} deliveries shown
                  </span>
                </p>
              )}
            </div>
            <img
              src={staticMapUrl}
              alt="Delivery zone map"
              className="w-full h-auto"
              style={{ minHeight: '300px', background: '#e5e7eb' }}
            />
          </div>
        )}

        {/* Admin-uploaded custom map snapshots — render below the auto map
            so the client sees both. Each click opens the lightbox. */}
        {(report.custom_map_snapshots || []).length > 0 && (
          <div
            className="bg-white rounded-xl shadow-md overflow-hidden mb-6"
            data-testid="report-custom-maps"
          >
            <div className="px-5 py-3 border-b">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <MapPin size={18} className="text-purple-600" />
                Additional Map Snapshots
                <span className="text-sm font-normal text-gray-400">
                  ({report.custom_map_snapshots.length})
                </span>
              </h2>
            </div>
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {report.custom_map_snapshots.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setLightboxPhoto(s.url)}
                  className="rounded overflow-hidden border bg-gray-100 hover:shadow transition"
                  data-testid={`custom-map-snapshot-${s.id}`}
                >
                  <img
                    src={s.url}
                    alt="Custom delivery map snapshot"
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Photos */}
        {report.photo_urls && report.photo_urls.length > 0 && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6" data-testid="report-photos">
            <div className="px-5 py-3 border-b">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Camera size={18} className="text-purple-600" />
                Delivery Photos
                <span className="text-sm font-normal text-gray-400">({report.photo_urls.length})</span>
              </h2>
            </div>
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {report.photo_urls.map((url, idx) => (
                <div
                  key={idx}
                  className="relative rounded-lg overflow-hidden bg-gray-100 cursor-pointer hover:ring-2 hover:ring-purple-400 transition-all"
                  style={{ paddingBottom: '133%' }}
                  onClick={() => setLightboxPhoto(url)}
                >
                  <img
                    src={url}
                    alt={`Delivery photo ${idx + 1}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ transform: 'rotate(90deg)', transformOrigin: 'center center' }}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary / Issues — only present on manual reports (legacy
            auto-reports don't carry these fields). */}
        {report._isManual && (report.summary || report.issues_text || (report.issues_checked || []).length > 0 || (report.admin_photos || []).length > 0) && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6" data-testid="report-summary">
            <div className="px-5 py-3 border-b">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <MessageSquare size={18} className="text-purple-600" />
                Delivery Summary
              </h2>
            </div>
            <div className="p-5 space-y-3">
              {report.summary && (
                <p className="text-sm text-gray-700 whitespace-pre-line">{report.summary}</p>
              )}
              {(report.issues_checked || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {report.issues_checked.map((iss) => (
                    <span key={iss} className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded">
                      {iss}
                    </span>
                  ))}
                </div>
              )}
              {report.issues_text && (
                <p className="text-sm text-gray-600 italic whitespace-pre-line">{report.issues_text}</p>
              )}
              {/* Admin-attached delivery photos. Shown UNDER the
                  summary block so the client reads the narrative first
                  then sees the evidence. Click to open lightbox. */}
              {(report.admin_photos || []).length > 0 && (
                <div className="pt-3 border-t" data-testid="report-admin-photos">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
                    <Camera size={12} />
                    Photos from the delivery ({report.admin_photos.length})
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {report.admin_photos.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setLightboxPhoto(p.url)}
                        className="rounded overflow-hidden bg-gray-100 hover:opacity-90 transition aspect-square"
                        data-testid={`admin-photo-${p.id}`}
                      >
                        <img
                          src={p.url}
                          alt="Delivery photo"
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Auditor Notes */}
        {report.auditor_notes_list && report.auditor_notes_list.length > 0 && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6" data-testid="report-auditor-notes">
            <div className="px-5 py-3 border-b">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <MessageSquare size={18} className="text-blue-600" />
                Auditor Notes
                <span className="text-sm font-normal text-gray-400">({report.auditor_notes_list.length})</span>
              </h2>
            </div>
            <div className="p-4 space-y-3">
              {report.auditor_notes_list.map((note, idx) => (
                <div key={note.id || idx} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold">
                      {(note.author || '?')[0].toUpperCase()}
                    </div>
                    <span className="text-sm font-semibold text-gray-800">{note.author}</span>
                    {note.created_at && (
                      <span className="text-xs text-gray-400">
                        {new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at {new Date(note.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap ml-9">{note.text}</p>
                  {note.photos && note.photos.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2 ml-9">
                      {note.photos.map((photoUrl, pi) => (
                        <img
                          key={pi}
                          src={`${API}${photoUrl}`}
                          alt={`Note attachment ${pi + 1}`}
                          className="w-24 h-24 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => setLightboxPhoto(`${API}${photoUrl}`)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-8 text-xs text-gray-400">
          <p>This report was generated by Local Flyer Delivery</p>
          <p className="mt-1">&copy; {new Date().getFullYear()} Local Flyer Delivery. All rights reserved.</p>
        </div>
      </div>

      {/* Photo Lightbox */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxPhoto(null)}
        >
          <img
            src={lightboxPhoto}
            alt="Full size delivery photo"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            style={{ transform: 'rotate(90deg)' }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setLightboxPhoto(null)}
            className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70"
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
};

export default ClientReportView;
